import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useT } from "@/lib/i18n";
import Logo from "@/components/Logo";
import { FileDown, MapPin, Phone, Mail, Linkedin, Sparkles, Cake, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SkillDots({ level }) {
  if (!level || level < 1) return null;
  return (
    <span className="font-mono text-xs text-neutral-400 ml-2">
      {"●".repeat(Math.min(level, 5))}{"○".repeat(Math.max(0, 5 - level))}
    </span>
  );
}

function fmtPeriod(exp, lang) {
  const f = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    if (!m) return y;
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { month: "short", year: "numeric" });
  };
  const start = f(exp.start);
  const end = exp.current ? (lang === "en" ? "present" : "heden") : f(exp.end);
  return [start, end].filter(Boolean).join(" — ");
}

export default function PublicCv() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    axios.get(`${API}/public/cv/${slug}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  useEffect(() => {
    if (data?.personal?.full_name) document.title = `${data.personal.full_name} — CV`;
    return () => { document.title = "JobGrid"; };
  }, [data]);

  if (state === "loading") {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center">
        <p className="ed-eyebrow">{t("loading")}</p>
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="ed-page min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="public-cv-notfound">
        <p className="font-serif text-4xl italic">404</p>
        <p className="text-neutral-600 mt-3">{t("pubNotFound")}</p>
        <Link to="/" className="ed-btn mt-8">{t("appName")}</Link>
      </div>
    );
  }

  const p = data.personal || {};
  const accent = data.accent || "#1A1A1A";
  const serif = data.font === "times";

  return (
    <div className="ed-page min-h-screen" data-testid="public-cv-page">
      {/* Top bar */}
      <header className="ed-header px-6 sm:px-10 py-4 flex items-center justify-between sticky top-0 bg-[#F9F8F6]/95 backdrop-blur z-20">
        <Logo size="md" showTagline={false} className="mr-3" />
        <a
          data-testid="public-cv-download"
          href={`${API}/public/cv/${slug}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="ed-btn"
        >
          <FileDown size={14} strokeWidth={1.5} /> {t("pubDownload")}
        </a>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-14">
        {/* Identity */}
        <section className="flex flex-col sm:flex-row items-start gap-8 pb-10 border-b-2" style={{ borderColor: accent }}>
          {data.photo && (
            <img
              src={data.photo}
              alt={p.full_name}
              className="w-32 h-32 object-cover border border-neutral-300"
              data-testid="public-cv-photo"
            />
          )}
          <div className="min-w-0">
            <h1
              className={`text-4xl sm:text-5xl tracking-tight ${serif ? "font-serif" : "font-sans font-semibold"}`}
              style={{ color: accent }}
              data-testid="public-cv-name"
            >
              {p.full_name}
            </h1>
            {p.title && (
              <p className="font-mono text-sm uppercase tracking-[0.25em] text-neutral-500 mt-3">{p.title}</p>
            )}
            {data.show_contact && (
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-sm text-neutral-600">
                {p.address && <span className="inline-flex items-center gap-1.5"><MapPin size={13} strokeWidth={1.5} />{p.address}</span>}
                {p.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} strokeWidth={1.5} />{p.phone}</span>}
                {p.email && <span className="inline-flex items-center gap-1.5"><Mail size={13} strokeWidth={1.5} />{p.email}</span>}
                {p.linkedin && <span className="inline-flex items-center gap-1.5"><Linkedin size={13} strokeWidth={1.5} />{p.linkedin}</span>}
                {p.birthdate && <span className="inline-flex items-center gap-1.5"><Cake size={13} strokeWidth={1.5} />{p.birthdate}</span>}
                {p.nationality && <span className="inline-flex items-center gap-1.5"><Flag size={13} strokeWidth={1.5} />{p.nationality}</span>}
              </div>
            )}
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_16rem] gap-x-14 gap-y-10 mt-10">
          {/* Main column */}
          <div className="space-y-10 min-w-0">
            {data.profile && (
              <section data-testid="public-cv-profile">
                <p className="ed-eyebrow mb-3" style={{ color: accent }}>{t("rbSecProfile")}</p>
                <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap text-neutral-800">{data.profile}</p>
              </section>
            )}

            {data.experiences.length > 0 && (
              <section data-testid="public-cv-experience">
                <p className="ed-eyebrow mb-4" style={{ color: accent }}>{t("rbSecExperience")}</p>
                <div className="space-y-6">
                  {data.experiences.map((exp, i) => (
                    <div key={exp.id || i} className="border-l-2 pl-5" style={{ borderColor: accent }}>
                      <p className="font-serif text-xl leading-tight">{exp.role}</p>
                      <p className="font-mono text-xs uppercase tracking-[0.12em] text-neutral-500 mt-1">
                        {[exp.company, exp.location].filter(Boolean).join(" · ")}
                        {fmtPeriod(exp, lang) && <> — {fmtPeriod(exp, lang)}</>}
                      </p>
                      {exp.description && (
                        <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap mt-2">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {data.education.length > 0 && (
              <section data-testid="public-cv-education">
                <p className="ed-eyebrow mb-4" style={{ color: accent }}>{t("rbSecEducation")}</p>
                <div className="space-y-3">
                  {data.education.map((edu, i) => (
                    <div key={edu.id || i}>
                      <p className="font-serif text-lg leading-tight">{edu.title}</p>
                      <p className="font-mono text-xs text-neutral-500 mt-0.5">
                        {[edu.level, edu.institution, edu.year].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Side column */}
          <aside className="space-y-8">
            {data.skills.length > 0 && (
              <section data-testid="public-cv-skills">
                <p className="ed-eyebrow mb-3" style={{ color: accent }}>{t("rbSecSkills")}</p>
                <ul className="space-y-1.5">
                  {data.skills.map((s, i) => (
                    <li key={i} className="text-sm text-neutral-800 flex items-baseline justify-between gap-2">
                      <span>{s.name}</span><SkillDots level={s.level} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {data.languages.length > 0 && (
              <section data-testid="public-cv-languages">
                <p className="ed-eyebrow mb-3" style={{ color: accent }}>{t("rbDndLanguages")}</p>
                <ul className="space-y-1.5">
                  {data.languages.map((s, i) => (
                    <li key={i} className="text-sm text-neutral-800 flex items-baseline justify-between gap-2">
                      <span>{s.name}</span><SkillDots level={s.level} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {data.hobbies.length > 0 && (
              <section data-testid="public-cv-hobbies">
                <p className="ed-eyebrow mb-3" style={{ color: accent }}>{t("rbSecHobbies")}</p>
                <ul className="space-y-1.5">
                  {data.hobbies.map((h, i) => <li key={i} className="text-sm text-neutral-800">{h}</li>)}
                </ul>
              </section>
            )}
            {data.references_text && (
              <section>
                <p className="ed-eyebrow mb-3" style={{ color: accent }}>{t("rbSecReferences")}</p>
                <p className="text-sm text-neutral-700 leading-relaxed">{data.references_text}</p>
              </section>
            )}
          </aside>
        </div>

        {/* CTA footer */}
        <footer className="mt-20 border-t border-neutral-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="public-cv-cta">
          <p className="font-serif italic text-neutral-600">
            {t("pubMadeWith")} <span className="font-semibold not-italic text-neutral-900">{t("appName")}</span>
          </p>
          <Link to="/" className="ed-btn">
            <Sparkles size={14} strokeWidth={1.5} /> {t("pubCta")}
          </Link>
        </footer>
      </main>
    </div>
  );
}
